import { useQuery, useMutation, useQueryClient, type UseQueryResult, type UseMutationResult } from 'react-query';
import { toast } from 'react-toastify';
import ToastComponent from '../../../components/toast';
import { type TableParamsType } from '../../../types/common';
import { AccessProfileFacade } from './accessProfileService';
import {
  type AccessProfileScope,
  type AccessProfileListResponse,
  type DeleteAccessProfileResponseDto,
  ACCESS_PROFILE_CONFIG,
} from './types';

export const useGetAccessProfiles = (
  scope: AccessProfileScope,
  handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>,
  tableInfo: TableParamsType,
  filterCustomValue?: string[]
): UseQueryResult<AccessProfileListResponse | undefined, unknown> => {
  const config = ACCESS_PROFILE_CONFIG[scope];
  const { filter, sorting, skipCount, maxResultCount } = tableInfo;

  return useQuery(
    [config.queryKey, filter, filterCustomValue, sorting, skipCount, maxResultCount],
    async () => {
      return await AccessProfileFacade.getList(scope, {
        filter,
        selectedPermissions: filterCustomValue ?? [],
        sorting,
        skipCount,
        maxResultCount,
      }, handleRequest);
    }
  );
};

export const usePostAccessProfile = (
  scope: AccessProfileScope,
  handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>,
  closeModal: () => void,
  id?: string
): UseMutationResult<any, unknown, any, unknown> => {
  const queryClient = useQueryClient();
  const config = ACCESS_PROFILE_CONFIG[scope];

  return useMutation(
    async (payload: any) => {
      if (id != null && id !== '') {
        return await AccessProfileFacade.update(scope, { ...payload, id }, handleRequest);
      }
      return await AccessProfileFacade.create(scope, payload, handleRequest);
    },
    {
      onSuccess: async (response) => {
        if (response != null) {
          await queryClient.invalidateQueries(config.queryKey);
          closeModal();
          toast.success(
            <ToastComponent
              label='Success'
              message={`${config.entityLabel} ${id != null && id !== '' ? 'edited.' : 'added.'}`}
            />
          );
        }
      },
      onError: (err) => {
        console.log('error', err);
      },
    }
  );
};

export const useDeleteAccessProfile = (
  scope: AccessProfileScope,
  handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>,
  closeModal: () => void
): UseMutationResult<any, unknown, string, unknown> => {
  const queryClient = useQueryClient();
  const config = ACCESS_PROFILE_CONFIG[scope];

  return useMutation(
    async (id: string) => {
      return await AccessProfileFacade.deleteConfirm(scope, id, handleRequest);
    },
    {
      onSuccess: (response) => {
        if (response != null) {
          void queryClient.invalidateQueries(config.queryKey);
          closeModal();
          toast.success(
            <ToastComponent
              label='Success'
              message={`${config.entityLabel} deleted.`}
            />
          );
        }
      },
      onError: () => {
        closeModal();
      },
    }
  );
};

